/* eslint-disable import/prefer-default-export */
import loadJS from "common/utils/request/loadjs";
import getMetaEnv from "common/utils/meta";
import { until, sleep } from "common/utils/helpers/delay";

export class RecaptchaHandler {
  #containerSelector = null;

  #recaptchaElementId = null;

  #recaptchaWidgetId = null;

  #isRecaptchaLoaded = false;

  #submitButton = null;

  #rememberMeCheckbox = null;

  #countdownActive = false;

  constructor(options = {}) {
    this.#containerSelector = options.containerSelector;
    this.#recaptchaElementId = options.recaptchaElementId;
    this.#submitButton = options.submitButton;
    this.#rememberMeCheckbox = options.rememberMeCheckbox;
  }

  get #$container() {
    return this.#containerSelector ? document.querySelector(this.#containerSelector) : null;
  }

  get #$recaptchaElement() {
    return this.#recaptchaElementId ? document.getElementById(this.#recaptchaElementId) : null;
  }

  static get #$recaptchaBubbleArrow() {
    return document.querySelector(".g-recaptcha-bubble-arrow");
  }

  hide() {
    if (!this.#$container) return;
    this.#$container.classList.add("hidden");
    this.#toggleFormElements(false);
    const recaptchaElement = this.#$recaptchaElement;
    if (recaptchaElement) {
      this.#clearRecaptchaWidget(recaptchaElement);
    }
  }

  isVisible() {
    return this.#$container && !this.#$container.classList.contains("hidden");
  }

  setCountdownState(active) {
    this.#countdownActive = active;
  }

  ensureButtonState() {
    if (this.isVisible()) {
      sleep(100).then(() => {
        const isValidated = this.isValidated();
        const shouldDisable = !isValidated || this.#countdownActive;
        this.#toggleFormElements(shouldDisable);
      });
    }
  }

  enableFormElements() {
    this.#toggleFormElements(false);
  }

  isValidated() {
    if (this.#recaptchaWidgetId === null || !window.grecaptcha) {
      return false;
    }

    try {
      const response = window.grecaptcha.getResponse(this.#recaptchaWidgetId);
      return response && response.length > 0;
    } catch (e) {
      return false;
    }
  }

  show() {
    if (!this.#$container) return;
    this.#$container.classList.remove("hidden");
    this.#toggleFormElements(true);
    this.#loadAndRenderRecaptcha();
  }

  reset() {
    const recaptchaElement = this.#$recaptchaElement;
    if (recaptchaElement) {
      this.#clearRecaptchaWidget(recaptchaElement, true);
    }
  }

  #toggleFormElements(disabled = false) {
    if (this.#submitButton) {
      this.#submitButton.disabled = disabled;
      this.#submitButton.classList.toggle("disabled", disabled);
      this.#submitButton.classList.toggle("recaptcha-disabled", disabled);
    }

    if (this.#rememberMeCheckbox) {
      this.#rememberMeCheckbox.disabled = disabled;
      this.#rememberMeCheckbox.classList.toggle("disabled", disabled);
    }
  }

  #loadAndRenderRecaptcha() {
    if (this.#isRecaptchaLoaded && window.grecaptcha && window.grecaptcha.render) {
      this.#renderRecaptcha();
      return;
    }

    if (!this.#isRecaptchaLoaded) {
      loadJS("https://www.recaptcha.net/recaptcha/api.js")
        .then(() => {
          this.#isRecaptchaLoaded = true;
          const checkReady = () => {
            if (window.grecaptcha && window.grecaptcha.render) {
              this.#renderRecaptcha();
            } else {
              sleep(100).then(checkReady);
            }
          };
          checkReady();
        })
        // eslint-disable-next-line no-console
        .catch(error => console.error("Failed to load reCAPTCHA script:", error));
    }
  }

  #renderRecaptcha() {
    let $recaptchaElement = this.#$recaptchaElement;

    if (!$recaptchaElement || !window.grecaptcha || !window.grecaptcha.render) return;
    if (RecaptchaHandler.#hasRecaptchaWidget($recaptchaElement)) return;
    this.#clearRecaptchaWidget($recaptchaElement);

    // Get fresh reference to the element after clearing (element was recreated)
    $recaptchaElement = this.#$recaptchaElement;
    if (!$recaptchaElement) return;

    try {
      const sitekey = getMetaEnv("recaptcha::public_key");
      if (!sitekey) {
        // eslint-disable-next-line no-console
        console.error("reCAPTCHA site key not found in meta tag");
        return;
      }
      this.#recaptchaWidgetId = window.grecaptcha.render($recaptchaElement, {
        sitekey,
        callback: () => {
          // Only enable if countdown is not active
          if (!this.#countdownActive) {
            this.#toggleFormElements(false);
          }
          RecaptchaHandler.#centerRecaptchaBubble();
        },
        "expired-callback": () => {
          this.#toggleFormElements(true);
        }
      });

      // Center the bubble immediately after rendering
      RecaptchaHandler.#centerRecaptchaBubble();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Failed to render reCAPTCHA:", e);
      this.#clearRecaptchaWidget(this.#$recaptchaElement, true);
    }
  }

  static #centerRecaptchaBubble() {
    until(() => RecaptchaHandler.#$recaptchaBubbleArrow, 100, 5000).then(found => {
      if (found) {
        const bubbleArrow = RecaptchaHandler.#$recaptchaBubbleArrow;
        if (bubbleArrow && bubbleArrow.parentElement) {
          const parent = bubbleArrow.parentElement;
          parent.classList.add("g-recaptcha-bubble-arrow-centered");
        }
      }
    });
  }

  static #hasRecaptchaWidget($container) {
    return (
      $container &&
      ($container.querySelector(".g-recaptcha") ||
        $container.querySelector("[data-sitekey]") ||
        $container.innerHTML.trim() !== "")
    );
  }

  #clearRecaptchaWidget($container, forceReset = false) {
    if (this.#recaptchaWidgetId !== null) {
      try {
        window.grecaptcha.reset(this.#recaptchaWidgetId);
      } catch (e) {
        // Widget might not exist anymore
      }
      this.#recaptchaWidgetId = null;
    }

    if ($container) {
      const { parentElement, id, className } = $container;
      const newElement = document.createElement("div");
      newElement.id = id;
      newElement.className = className;
      parentElement.replaceChild(newElement, $container);
    }

    if (forceReset) this.#toggleFormElements(true);
  }
}
